/**
 * Module-level singleton store for Ghostwriter frontend data.
 *
 * State lives outside of React so it can be shared across independent
 * React trees (e.g. Ghostwriter and MyJobs each have their own
 * `registerComponent` root). Components subscribe via
 * `useSyncExternalStore` in the companion hooks module.
 *
 * The `AsyncState<T>` shape intentionally mirrors what GraphQL query
 * hooks return (`{ data, loading, error }`), so generated hooks can
 * feed directly into the store setters when they are introduced.
 */

import type { ScheduleApplyConsentStatusResponse } from '../models/ScheduleApplyConsentStatus';
import type { ScheduleApplyJob, ScheduleApplyResponseGroupInstanceSeed } from '../models/ScheduleApplyJob';

import { type AsyncState , initialAsyncState } from './AsyncState';
import {
    collectEmailConfirmationMismatches,
    emailsMatchForConfirmation,
    findEmailFieldToConfirm,
    hydrateGroupInstanceFields,
    isConfirmEmailField,
    isFieldValid,
    isFieldVisible,
    isOptionsField,
    isUnboundField,
} from './GhostwriterValidation';
import { type ScheduleApplyFormFieldBase, type ScheduleApplyFormFieldGroup , buildInstancedFieldId, FieldType } from '../models/ScheduleApplyFormField';
import { relayIdSuffix } from '../utils/relayId';


// ---------------------------------------------------------------------------
// Draft types
// ---------------------------------------------------------------------------

export type DraftFieldValue = string | string[];

/**
 * Sentinel used as the first instance ID for every group (repeatable or not).
 * Field keys for this instance use the bare field ID (no suffix), preserving
 * backward compatibility with non-repeatable groups and pre-existing draft
 * payloads. Only secondary instance IDs are used as suffixes.
 */
export const PRIMARY_INSTANCE_ID = '__primary__';

export interface DraftState {
    applicationId: string;
    fieldUpdates: Map<string, DraftFieldValue>;
    /**
     * Field IDs whose validation errors should be visible in the UI.
     * A field appears here only if it is both currently invalid AND has
     * been "attempted" (the user pressed Continue or Submit).
     */
    fieldValidationErrors: Set<string>;
    /**
     * For each group, the ordered list of instance IDs the user has authored.
     * The first entry is always `PRIMARY_INSTANCE_ID` (its fields use bare draft
     * keys); subsequent entries are stable strings used as key suffixes.
     *
     * Non-repeatable groups always have exactly one entry (the primary).
     * Repeatable groups (`group.canCreateNewResponseGroup === true`) may
     * grow without an explicit upper bound.
     */
    groupInstances: Map<string, string[]>;
}

/**
 * Tracks field IDs the user has attempted to submit past (Continue or Submit).
 * Kept outside DraftState because it's an implementation detail — UI components
 * only read `fieldValidationErrors` which is the intersection of this set
 * with the set of currently-invalid fields.
 */
let attemptedFieldIds = new Set<string>();

/**
 * Per-application failed-submission counts for the session. Kept outside
 * `DraftState` and not cleared by `discardDraft` so a failure that closes the
 * flow still counts toward the stuck threshold when the form is reopened.
 */
let submissionFailureCounts = new Map<string, number>();

/** Records one submission failure for the application and returns the new count. */
export function recordSubmissionFailure(applicationId: string): number {
    const next = (submissionFailureCounts.get(applicationId) ?? 0) + 1;
    submissionFailureCounts.set(applicationId, next);
    return next;
}

/** Clears the submission-failure streak for the application (e.g. after success). */
export function resetSubmissionFailures(applicationId: string): void {
    submissionFailureCounts.delete(applicationId);
}

// ---------------------------------------------------------------------------
// Store state
// ---------------------------------------------------------------------------

export const GHOSTWRITER_TTL_MS = 5 * 60 * 1000; // 5 minutes

export interface GhostwriterStoreState {
    isGhostwriterActive: boolean | null;
    /**
     * Phase A (synchronous flow) proctor allocation. Tracked separately
     * from `isGhostwriterActive` so components can branch UI/copy on
     * which variant the user is in without conflating gating concerns.
     * `null` while the proctor check is still resolving.
     */
    isGhostwriterPhaseA: boolean | null;
    /**
     * Phase B (skip-eligibility flow) proctor allocation. Tracked
     * separately so `useGhostwriterForViewJob` can skip the eligibility
     * backend call when the jobKey is already pre-hydrated into
     * `autoAppliableJobKeys` from the phase B payload. `null` while the
     * proctor check is still resolving.
     */
    isGhostwriterPhaseB: boolean | null;
    /**
     * Phase C proctor allocation. Reuses phase A's Prefill synchronous flow
     * but keeps the standard 'Auto apply' CTA copy, so it is tracked
     * separately from `isGhostwriterPhaseA`. `null` while the proctor check
     * is still resolving. See RN-5091.
     */
    isGhostwriterPhaseC: boolean | null;
    autoAppliableJobKeys: AsyncState<Set<string>>;
    autoAppliableKeysFetchedAt: number | null;
    jobs: AsyncState<ScheduleApplyJob[]>;
    consentStatus: AsyncState<ScheduleApplyConsentStatusResponse>;
    draft: DraftState | null;
}

type Listener = () => void;

let state: GhostwriterStoreState = {
    isGhostwriterActive: null,
    isGhostwriterPhaseA: null,
    isGhostwriterPhaseB: null,
    isGhostwriterPhaseC: null,
    autoAppliableJobKeys: initialAsyncState<Set<string>>(),
    autoAppliableKeysFetchedAt: null,
    jobs: initialAsyncState<ScheduleApplyJob[]>(),
    consentStatus: initialAsyncState<ScheduleApplyConsentStatusResponse>(),
    draft: null,
};

const listeners = new Set<Listener>();

function emit() {
    for (const listener of listeners) {
        listener();
    }
}

// ---------------------------------------------------------------------------
// Subscription
// ---------------------------------------------------------------------------

export function subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

export function getSnapshot(): GhostwriterStoreState {
    return state;
}

const serverSnapshot: GhostwriterStoreState = Object.freeze({
    isGhostwriterActive: null,
    isGhostwriterPhaseA: null,
    isGhostwriterPhaseB: null,
    isGhostwriterPhaseC: null,
    autoAppliableJobKeys: initialAsyncState<Set<string>>(),
    autoAppliableKeysFetchedAt: null,
    jobs: initialAsyncState<ScheduleApplyJob[]>(),
    consentStatus: initialAsyncState<ScheduleApplyConsentStatusResponse>(),
    draft: null,
});

export function getServerSnapshot(): GhostwriterStoreState {
    return serverSnapshot;
}

// ---------------------------------------------------------------------------
// Lifecycle mutations
// ---------------------------------------------------------------------------

export function setGhostwriterActive(value: boolean | null): void {
    if (state.isGhostwriterActive === value) return;
    state = { ...state, isGhostwriterActive: value };
    emit();
}

export function setGhostwriterPhaseA(value: boolean | null): void {
    if (state.isGhostwriterPhaseA === value) return;
    state = { ...state, isGhostwriterPhaseA: value };
    emit();
}

export function setGhostwriterPhaseB(value: boolean | null): void {
    if (state.isGhostwriterPhaseB === value) return;
    state = { ...state, isGhostwriterPhaseB: value };
    emit();
}

export function setGhostwriterPhaseC(value: boolean | null): void {
    if (state.isGhostwriterPhaseC === value) return;
    state = { ...state, isGhostwriterPhaseC: value };
    emit();
}

// ---------------------------------------------------------------------------
// Auto-appliable job keys mutations
// ---------------------------------------------------------------------------

export function setAutoAppliableJobKeys(next: AsyncState<Set<string>>): void {
    state = {
        ...state,
        autoAppliableJobKeys: next,
        autoAppliableKeysFetchedAt: next.data !== null ? Date.now() : state.autoAppliableKeysFetchedAt,
    };
    emit();
}

export function addJobKeyToAutoAppliableSet(jobKey: string): void {
    const existing = state.autoAppliableJobKeys.data ?? new Set();
    const next = new Set(existing);
    next.add(jobKey);
    state = { ...state, autoAppliableJobKeys: { data: next, loading: false, error: null } };
    emit();
}

export function isAutoAppliableKeysFresh(): boolean {
    if (state.autoAppliableKeysFetchedAt == null) return false;
    return Date.now() - state.autoAppliableKeysFetchedAt < GHOSTWRITER_TTL_MS;
}

// ---------------------------------------------------------------------------
// Jobs & consent mutations
// ---------------------------------------------------------------------------

export function setJobsState(next: AsyncState<ScheduleApplyJob[]>): void {
    state = { ...state, jobs: next };
    emit();
}

/** Upserts by `jobKey`: replaces the first row with that key, or appends if none. */
export function addJobToStore(job: ScheduleApplyJob): void {
    const existing = state.jobs.data ?? [];
    const idx = existing.findIndex((j) => j.jobKey === job.jobKey);
    let next: ScheduleApplyJob[];
    if (idx >= 0) {
        next = [...existing];
        next[idx] = job;
    } else {
        next = [...existing, job];
    }
    state = {
        ...state,
        jobs: { data: next, loading: false, error: null },
    };
    emit();
}

export function setConsentStatusState(next: AsyncState<ScheduleApplyConsentStatusResponse>): void {
    state = { ...state, consentStatus: next };
    emit();
}

export function updateJob(applicationId: string, updates: Partial<ScheduleApplyJob>): void {
    const { jobs } = state;
    if (!jobs.data) return;

    const updatedJobs = jobs.data.map((job) =>
        job.applicationId === applicationId ? { ...job, ...updates } : job,
    );

    state = { ...state, jobs: { ...jobs, data: updatedJobs } };
    emit();
}

// ---------------------------------------------------------------------------
// Draft helpers
// ---------------------------------------------------------------------------

/**
 * Walks form field groups (including nested groups) and collects every field's
 * current response value, keyed under the *primary* instance ID (i.e. bare
 * field ID). Also seeds `groupInstances` with a primary entry per group, plus
 * one additional entry per persisted secondary response group (sourced from
 * `responseGroupSeeds`) for repeatable groups.
 */
function extractDraftSeed(
    groups: ScheduleApplyFormFieldGroup[],
    responseGroupSeeds:
        | ReadonlyMap<string, ReadonlyArray<ScheduleApplyResponseGroupInstanceSeed>>
        | undefined,
): { fieldUpdates: Map<string, DraftFieldValue>; groupInstances: Map<string, string[]> } {
    const fieldUpdates = new Map<string, DraftFieldValue>();
    const groupInstances = new Map<string, string[]>();

    function walk(groups: ScheduleApplyFormFieldGroup[]): void {
        for (const group of groups) {
            const instances = groupInstances.get(group.groupId) ?? [PRIMARY_INSTANCE_ID];
            if (!groupInstances.has(group.groupId)) {
                groupInstances.set(group.groupId, instances);
            }

            for (const field of group.formFields) {
                if (isUnboundField(field)) {
                    fieldUpdates.set(field.id, field.response.responseValue);
                } else if (isOptionsField(field)) {
                    fieldUpdates.set(field.id, [...field.response.responseValues]);
                }
            }

            // Seed additional instances for repeatable groups whose persisted
            // response set carries more than one entry. Each seed becomes a
            // new instance whose draft keys are `${field.id}#${instanceId}`.
            const seeds = responseGroupSeeds?.get(group.groupId);
            if (seeds && seeds.length > 0) {
                for (const seed of seeds) {
                    instances.push(seed.instanceId);
                    for (const field of group.formFields) {
                        const value = seed.fieldValues.get(relayIdSuffix(field.id));
                        if (value === undefined) continue;
                        const key = buildInstancedFieldId(field.id, seed.instanceId);
                        fieldUpdates.set(key, Array.isArray(value) ? [...value] : value);
                    }
                }
            }

            if (group.nestedFormFieldGroup) {
                walk([group.nestedFormFieldGroup]);
            }
        }
    }

    walk(groups);
    return { fieldUpdates, groupInstances };
}

// ---------------------------------------------------------------------------
// Draft mutations
// ---------------------------------------------------------------------------

/**
 * Initializes a draft for the given application by extracting the
 * current field response values from the committed store. Throws
 * if the application is not found.
 */
export function beginDraft(applicationId: string): void {
    const job = state.jobs.data?.find((j) => j.applicationId === applicationId);
    if (!job) {
        throw new Error(`beginDraft: application "${applicationId}" not found in store`);
    }

    attemptedFieldIds = new Set();

    const { fieldUpdates, groupInstances } = extractDraftSeed(
        job.applicationFormFieldGroups,
        job.responseGroupSeeds,
    );

    state = {
        ...state,
        draft: {
            applicationId,
            fieldUpdates,
            fieldValidationErrors: new Set(),
            groupInstances,
        },
    };
    emit();
}

/**
 * Updates a single field value in the active draft. Throws if no
 * draft is active.
 *
 * `instancedFieldId` is the bare `field.id` for primary-instance writes, and
 * `${fieldId}#${suffix}` for non-primary instances of repeatable groups.
 */
export function updateDraftField(instancedFieldId: string, value: DraftFieldValue): void {
    if (!state.draft) {
        throw new Error('updateDraftField: no active draft');
    }

    const nextUpdates = new Map(state.draft.fieldUpdates);
    nextUpdates.set(instancedFieldId, value);

    const nextErrors = new Set(state.draft.fieldValidationErrors);

    // Resolve the changed field once so we can (a) revalidate it and (b)
    // walk only its sibling *dependents* (fields whose `condition.fieldName`
    // points at this field). We hydrate the group instance a single time
    // and reuse it for every sibling we revalidate, so a keystroke costs
    // O(group size) rather than O(attemptedFields × totalFields).
    const job = getJobForActiveDraft();
    const fieldId = stripInstanceSuffix(instancedFieldId);
    const suffix = extractInstanceSuffix(instancedFieldId);
    const located = job ? findFieldAndGroup(job.applicationFormFieldGroups, fieldId) : undefined;

    if (!located) {
        // Field not in the schema (e.g. orphaned key). Fall back to a single
        // revalidation of just this key, matching the pre-condition behavior.
        if (attemptedFieldIds.has(instancedFieldId)) {
            revalidateField(instancedFieldId, value, nextErrors, nextUpdates);
        }
    } else {
        const hydratedSiblings = hydrateGroupInstanceFields(located.group, suffix, nextUpdates);
        const selfIndex = located.group.formFields.findIndex((f) => f.id === fieldId);

        if (attemptedFieldIds.has(instancedFieldId)) {
            revalidateFieldWithSiblings(
                instancedFieldId,
                value,
                located.field,
                hydratedSiblings[selfIndex] ?? located.field,
                hydratedSiblings,
                nextErrors,
            );
        }

        // Only siblings whose condition references *this* field's `fieldName`
        // can possibly have flipped visibility. The changed field's
        // `fieldName` may be null on legacy fields with no fieldName — in
        // which case nothing can depend on it.
        const changedFieldName = located.field.fieldName;
        if (changedFieldName != null && changedFieldName !== '') {
            for (let i = 0; i < located.group.formFields.length; i++) {
                if (i === selfIndex) continue;
                const sibling = located.group.formFields[i];
                if (sibling.condition?.fieldName !== changedFieldName) continue;

                const siblingKey = buildInstancedFieldId(sibling.id, suffix);
                if (!attemptedFieldIds.has(siblingKey)) continue;

                revalidateFieldWithSiblings(
                    siblingKey,
                    nextUpdates.get(siblingKey),
                    sibling,
                    hydratedSiblings[i] ?? sibling,
                    hydratedSiblings,
                    nextErrors,
                );
            }
        }

        // Cross-field re-check for the (email, confirm-email) pair affected
        // by this keystroke. Without this, a user fixing a previously-flagged
        // mismatch would have to press Submit again to clear the error;
        // recomputing the pair each keystroke (within the same group
        // instance) keeps the "Email addresses must match" indicator live.
        revalidateConfirmEmailPairForGroup(
            located.group,
            selfIndex,
            hydratedSiblings,
            suffix,
            nextUpdates,
            nextErrors,
        );
    }

    state = {
        ...state,
        draft: { ...state.draft, fieldUpdates: nextUpdates, fieldValidationErrors: nextErrors },
    };
    emit();
}

// ---------------------------------------------------------------------------
// Repeatable group instance mutations
// ---------------------------------------------------------------------------

/** Monotonic counter used to mint stable instance IDs within a session. */
let instanceCounter = 0;

/**
 * Appends a new instance to the given group in the active draft and returns
 * the newly generated instance ID. Returns `null` when:
 *
 * - there is no active draft,
 * - the group cannot be located in the underlying job (e.g. unknown
 *   `groupId`), or
 * - the group is not flagged `canCreateNewResponseGroup`. Enforced here in
 *   addition to the renderer so non-UI callers (deferred triggers, retries,
 *   future automation) cannot create instances on a non-repeatable group
 *   and produce a malformed submit payload.
 *
 * The new instance owns no field values yet — callers can begin writing
 * under `${fieldId}#${returnedInstanceId}` keys.
 */
export function addGroupInstance(groupId: string): string | null {
    if (!state.draft) return null;

    const job = getJobForActiveDraft();
    const groupDef = job ? findGroupById(job.applicationFormFieldGroups, groupId) : undefined;
    if (!groupDef) return null;
    if (!groupDef.canCreateNewResponseGroup) return null;

    const existing = state.draft.groupInstances.get(groupId) ?? [PRIMARY_INSTANCE_ID];

    const newInstanceId = `inst-${++instanceCounter}`;
    const next = new Map(state.draft.groupInstances);
    next.set(groupId, [...existing, newInstanceId]);

    state = {
        ...state,
        draft: { ...state.draft, groupInstances: next },
    };
    emit();
    return newInstanceId;
}

/**
 * Removes a non-primary instance from the given group in the active draft and
 * deletes any draft field values keyed under that instance. The primary
 * instance (`PRIMARY_INSTANCE_ID`) cannot be removed.
 */
export function removeGroupInstance(groupId: string, instanceId: string): void {
    if (!state.draft) return;
    if (instanceId === PRIMARY_INSTANCE_ID) return;

    const existing = state.draft.groupInstances.get(groupId);
    if (!existing || !existing.includes(instanceId)) return;

    const nextInstances = new Map(state.draft.groupInstances);
    nextInstances.set(
        groupId,
        existing.filter((id) => id !== instanceId),
    );

    // Use exact-suffix comparison rather than `key.endsWith('#' + instanceId)`:
    // a future instance ID scheme allowing `#` inside an ID, or an instance
    // ID that happens to be the suffix of another, would otherwise silently
    // delete the wrong fields. The bare-key (primary-instance) form has no
    // `#` and is correctly excluded by `extractInstanceSuffix` returning null.
    const matchesInstance = (key: string): boolean =>
        extractInstanceSuffix(key) === instanceId;

    const nextUpdates = new Map(state.draft.fieldUpdates);
    for (const key of nextUpdates.keys()) {
        if (matchesInstance(key)) nextUpdates.delete(key);
    }

    const nextErrors = new Set(state.draft.fieldValidationErrors);
    for (const key of [...nextErrors]) {
        if (matchesInstance(key)) nextErrors.delete(key);
    }
    for (const key of [...attemptedFieldIds]) {
        if (matchesInstance(key)) attemptedFieldIds.delete(key);
    }

    state = {
        ...state,
        draft: {
            ...state.draft,
            groupInstances: nextInstances,
            fieldUpdates: nextUpdates,
            fieldValidationErrors: nextErrors,
        },
    };
    emit();
}

/**
 * Returns a read-only copy of the active draft's field updates and per-group
 * instance metadata for building an API submission payload. Returns `null`
 * if no draft is active. Does not modify store state.
 */
export function getDraftFieldUpdates(): {
    applicationId: string;
    fieldUpdates: ReadonlyMap<string, DraftFieldValue>;
    groupInstances: ReadonlyMap<string, ReadonlyArray<string>>;
} | null {
    if (!state.draft) return null;

    return {
        applicationId: state.draft.applicationId,
        fieldUpdates: new Map(state.draft.fieldUpdates),
        groupInstances: new Map(
            Array.from(state.draft.groupInstances, ([k, v]) => [k, [...v]]),
        ),
    };
}

/**
 * Returns true if the active draft contains any field values that differ
 * from the committed job data. Compares the draft's fieldUpdates map
 * against the original response values extracted from the store's job.
 * Returns false when there is no active draft.
 */
export function hasDraftChanges(): boolean {
    if (!state.draft) return false;

    const job = state.jobs.data?.find((j) => j.applicationId === state.draft!.applicationId);
    if (!job) return false;

    const { fieldUpdates: originals } = extractDraftSeed(
        job.applicationFormFieldGroups,
        job.responseGroupSeeds,
    );
    const { fieldUpdates } = state.draft;

    for (const [fieldId, draftValue] of fieldUpdates) {
        const originalValue = originals.get(fieldId);
        if (Array.isArray(draftValue) && Array.isArray(originalValue)) {
            if (draftValue.length !== originalValue.length || draftValue.some((v, i) => v !== originalValue[i])) {
                return true;
            }
        } else if (draftValue !== originalValue) {
            return true;
        }
    }

    // Any non-primary instance with at least one draft field is also a change.
    for (const [, instances] of state.draft.groupInstances) {
        if (instances.length > 1) return true;
    }

    return false;
}

/**
 * Clears the attempted-fields tracking and any visible validation errors
 * from the current draft. Called when transitioning to a new phase of the
 * flow (e.g. entering the full review screen) so stale errors from earlier
 * question screens don't carry over.
 */
export function resetAttemptedFields(): void {
    attemptedFieldIds = new Set();
    if (!state.draft) return;

    if (state.draft.fieldValidationErrors.size > 0) {
        state = {
            ...state,
            draft: { ...state.draft, fieldValidationErrors: new Set() },
        };
        emit();
    }
}

// ---------------------------------------------------------------------------
// Draft validation helpers
// ---------------------------------------------------------------------------

function getJobForActiveDraft(): ScheduleApplyJob | undefined {
    const { draft } = state;
    if (!draft) return undefined;
    return state.jobs.data?.find((j) => j.applicationId === draft.applicationId);
}

/**
 * Locates the field with the given `fieldId` and returns it together with
 * its directly-containing group (so callers can scope sibling lookups for
 * conditional-visibility evaluation). Returns `undefined` if not found.
 */
function findFieldAndGroup(
    groups: ScheduleApplyFormFieldGroup[],
    fieldId: string,
): { field: ScheduleApplyFormFieldBase; group: ScheduleApplyFormFieldGroup } | undefined {
    for (const group of groups) {
        for (const field of group.formFields) {
            if (field.id === fieldId) return { field, group };
        }
        if (group.nestedFormFieldGroup) {
            const found = findFieldAndGroup([group.nestedFormFieldGroup], fieldId);
            if (found) return found;
        }
    }
    return undefined;
}

/**
 * Locates the group with the given `groupId` anywhere in the tree (including
 * nested groups). Returns `undefined` if not found.
 */
function findGroupById(
    groups: ScheduleApplyFormFieldGroup[],
    groupId: string,
): ScheduleApplyFormFieldGroup | undefined {
    for (const group of groups) {
        if (group.groupId === groupId) return group;
        if (group.nestedFormFieldGroup) {
            const found = findGroupById([group.nestedFormFieldGroup], groupId);
            if (found) return found;
        }
    }
    return undefined;
}

/**
 * Strips an instance suffix from a draft key, returning the underlying field
 * ID. Inverse of `buildInstancedFieldId`.
 */
function stripInstanceSuffix(instancedFieldId: string): string {
    const idx = instancedFieldId.indexOf('#');
    return idx === -1 ? instancedFieldId : instancedFieldId.slice(0, idx);
}

/**
 * Returns the instance suffix encoded in an instanced field id, or `null`
 * for primary-instance keys.
 */
function extractInstanceSuffix(instancedFieldId: string): string | null {
    const idx = instancedFieldId.indexOf('#');
    return idx === -1 ? null : instancedFieldId.slice(idx + 1);
}

/**
 * Validates a single field against the job definition and updates the
 * given errors set in place. Returns whether the field is valid.
 *
 * `instancedFieldId` is the draft-state key (possibly `${fieldId}#${suffix}`);
 * the underlying field is resolved by stripping the suffix.
 *
 * Conditional-visibility check: if the field has a `condition` that evaluates
 * to "hidden" given the *current* draft state (`fieldUpdatesSnapshot`), the
 * field is treated as valid (and removed from errors) — hidden fields must
 * never block submission.
 */
function revalidateField(
    instancedFieldId: string,
    value: DraftFieldValue | undefined,
    errors: Set<string>,
    fieldUpdatesSnapshot: ReadonlyMap<string, DraftFieldValue>,
): boolean {
    const job = getJobForActiveDraft();
    const fieldId = stripInstanceSuffix(instancedFieldId);
    const found = job ? findFieldAndGroup(job.applicationFormFieldGroups, fieldId) : undefined;
    if (!found) return true;

    const suffix = extractInstanceSuffix(instancedFieldId);
    const hydratedSiblings = hydrateGroupInstanceFields(
        found.group,
        suffix,
        fieldUpdatesSnapshot,
    );
    const hydratedSelf =
        hydratedSiblings[found.group.formFields.findIndex((f) => f.id === fieldId)] ??
        found.field;

    return revalidateFieldWithSiblings(
        instancedFieldId,
        value,
        found.field,
        hydratedSelf,
        hydratedSiblings,
        errors,
    );
}

/**
 * Variant of {@link revalidateField} that takes already-resolved field +
 * hydrated siblings, avoiding redundant tree walks and group hydrations
 * when the caller is iterating multiple fields in the same group instance
 * (see `updateDraftField`).
 */
function revalidateFieldWithSiblings(
    instancedFieldId: string,
    value: DraftFieldValue | undefined,
    field: ScheduleApplyFormFieldBase,
    hydratedSelf: ScheduleApplyFormFieldBase,
    hydratedSiblings: ReadonlyArray<ScheduleApplyFormFieldBase>,
    errors: Set<string>,
): boolean {
    if (!isFieldVisible(hydratedSelf, hydratedSiblings)) {
        errors.delete(instancedFieldId);
        return true;
    }

    const valid = isFieldValid(field, value);
    if (valid) {
        errors.delete(instancedFieldId);
    } else {
        errors.add(instancedFieldId);
    }
    return valid;
}

/**
 * Re-evaluates the confirm-email mismatch error for the (email, confirm-email)
 * pair touched by a keystroke and updates `errors` in place.
 *
 * Live re-evaluation only fires for fields that have already been "attempted"
 * (i.e. the user has tried to submit at least once, putting them in the
 * fieldValidationErrors-visible state). This preserves the established UX
 * rule that we don't pop validation errors on first edit; once the user has
 * seen the error, it updates as they type.
 *
 * Strictly scoped to the changed field's group instance — repeatable groups
 * have independent (email, confirm-email) pairs and never cross-validate.
 */
function revalidateConfirmEmailPairForGroup(
    group: ScheduleApplyFormFieldGroup,
    changedIndex: number,
    hydratedSiblings: ReadonlyArray<ScheduleApplyFormFieldBase>,
    suffix: string | null,
    fieldUpdates: ReadonlyMap<string, DraftFieldValue>,
    errors: Set<string>,
): void {
    const changed = hydratedSiblings[changedIndex];
    if (!changed || changed.fieldType !== FieldType.Email) return;

    // Identify the confirm-email field affected by this keystroke. The
    // changed field is either the confirm-email itself, or the primary
    // email that one or more confirm-email siblings reference.
    const confirmIndices: number[] = [];
    if (isConfirmEmailField(changed)) {
        confirmIndices.push(changedIndex);
    } else {
        for (let i = 0; i < hydratedSiblings.length; i++) {
            if (i === changedIndex) continue;
            const sibling = hydratedSiblings[i];
            if (!isConfirmEmailField(sibling)) continue;
            const target = findEmailFieldToConfirm(sibling, hydratedSiblings);
            if (target?.id === changed.id) confirmIndices.push(i);
        }
    }

    for (const idx of confirmIndices) {
        const confirm = hydratedSiblings[idx];
        const confirmKey = buildInstancedFieldId(group.formFields[idx].id, suffix);
        if (!attemptedFieldIds.has(confirmKey)) continue;
        if (!isFieldVisible(confirm, hydratedSiblings)) {
            errors.delete(confirmKey);
            continue;
        }

        // Whenever we *cannot* render a mismatch verdict on this pair (no
        // target, or either value empty), we must still clear any stale
        // mismatch error left over from a previous keystroke — otherwise the
        // confirm field would be "stuck red" after the user clears the
        // primary email or otherwise removes the comparison basis. We only
        // clear when the confirm field's own per-field check is passing, so
        // a legitimate Required/format error from `revalidateField` stays.
        const clearStaleMismatchIfOtherwiseValid = () => {
            if (isFieldValid(confirm, fieldUpdates.get(confirmKey))) {
                errors.delete(confirmKey);
            }
        };

        const target = findEmailFieldToConfirm(confirm, hydratedSiblings);
        if (!target) {
            clearStaleMismatchIfOtherwiseValid();
            continue;
        }

        // Read straight from the hydrated field response: hydration already
        // has the latest `nextUpdates` merged in (caller passes
        // `hydrateGroupInstanceFields(group, suffix, nextUpdates)`).
        const confirmValue = isUnboundField(confirm) ? confirm.response.responseValue.trim() : '';
        const targetValue = isUnboundField(target) ? target.response.responseValue.trim() : '';

        if (confirmValue === '' || targetValue === '') {
            clearStaleMismatchIfOtherwiseValid();
            continue;
        }

        if (emailsMatchForConfirmation(confirmValue, targetValue)) {
            clearStaleMismatchIfOtherwiseValid();
        } else {
            errors.add(confirmKey);
        }
    }
}

function updateDraftErrors(nextErrors: Set<string>): void {
    const { draft } = state;
    if (!draft) return;

    state = {
        ...state,
        draft: { ...draft, fieldValidationErrors: nextErrors },
    };
    emit();
}

// ---------------------------------------------------------------------------
// Draft validation
// ---------------------------------------------------------------------------

/**
 * Validates a single draft field against the job's field definition.
 * Marks the field as attempted, updates `fieldValidationErrors`, and
 * returns whether the field is valid.
 *
 * `instancedFieldId` is the draft-state key (bare `field.id` for primary
 * instance, `${fieldId}#${suffix}` for non-primary).
 */
export function validateDraftField(instancedFieldId: string): boolean {
    if (!state.draft) return false;

    attemptedFieldIds.add(instancedFieldId);

    const nextErrors = new Set(state.draft.fieldValidationErrors);
    const valid = revalidateField(
        instancedFieldId,
        state.draft.fieldUpdates.get(instancedFieldId),
        nextErrors,
        state.draft.fieldUpdates,
    );
    updateDraftErrors(nextErrors);
    return valid;
}

/**
 * Validates all fields in the active draft against the job's field
 * definitions. Marks every field as attempted, populates
 * `fieldValidationErrors` with all invalid field IDs, and returns
 * whether all fields are valid.
 */
export function validateAllDraftFields(): boolean {
    const { draft } = state;
    if (!draft) return false;

    const job = getJobForActiveDraft();
    if (!job) return false;

    const nextErrors = new Set<string>();
    const { fieldUpdates, groupInstances } = draft;

    function walkGroups(groups: ScheduleApplyFormFieldGroup[]) {
        for (const group of groups) {
            const instances = groupInstances.get(group.groupId) ?? [PRIMARY_INSTANCE_ID];
            for (let i = 0; i < instances.length; i++) {
                const suffix = i === 0 ? null : instances[i];
                for (const field of group.formFields) {
                    const key = buildInstancedFieldId(field.id, suffix);
                    attemptedFieldIds.add(key);
                    revalidateField(key, fieldUpdates.get(key), nextErrors, fieldUpdates);
                }
            }
            if (group.nestedFormFieldGroup) {
                walkGroups([group.nestedFormFieldGroup]);
            }
        }
    }

    walkGroups(job.applicationFormFieldGroups);

    // Cross-field check: confirm-email fields whose value differs from their
    // matched primary email field. Per-field validation can't detect this on
    // its own (it never sees siblings); we surface mismatches as standard
    // validation errors so the existing scroll-to-first-error and submit-
    // blocking flow apply uniformly.
    const mismatches = collectEmailConfirmationMismatches(
        job.applicationFormFieldGroups,
        fieldUpdates,
        groupInstances,
    );
    for (const id of mismatches) nextErrors.add(id);

    updateDraftErrors(nextErrors);
    return nextErrors.size === 0;
}

/**
 * Discards the active draft without modifying committed state.
 * Call this after the server response has been written to the
 * store via `setJobsState` or `updateJob`. No-op if no draft
 * is active.
 */
export function discardDraft(): void {
    if (!state.draft) return;

    attemptedFieldIds = new Set();
    state = { ...state, draft: null };
    emit();
}

// ---------------------------------------------------------------------------
// Test utility
// ---------------------------------------------------------------------------

/**
 * Resets the store to its initial state. Intended for use in tests
 * to ensure isolation between test cases.
 */
export function resetStore(): void {
    attemptedFieldIds = new Set();
    instanceCounter = 0;
    submissionFailureCounts = new Map();
    state = {
        isGhostwriterActive: null,
        isGhostwriterPhaseA: null,
        isGhostwriterPhaseB: null,
        isGhostwriterPhaseC: null,
        autoAppliableJobKeys: initialAsyncState<Set<string>>(),
        autoAppliableKeysFetchedAt: null,
        jobs: initialAsyncState<ScheduleApplyJob[]>(),
        consentStatus: initialAsyncState<ScheduleApplyConsentStatusResponse>(),
        draft: null,
    };
    emit();
}
